import { useSelector } from 'react-redux'
import { FormControl, Input, InputLabel } from '@mui/material'
import type { SelectChangeEvent } from '@mui/material/Select'
import {
  type Field,
  NumberUtils,
  PuiSelect,
  type PuiSelectProps,
} from '@pbt/pbt-ui-components'

import FeatureToggle from '~/constants/featureToggle'
import { getCurrentBusinessTaxes } from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'

export type TaxRateSelectProps = PuiSelectProps & {
  field: Field
  taxableField: Field
}

const TaxRateSelect = ({
  taxableField: _taxableField,
  field,
  ...rest
}: TaxRateSelectProps) => {
  const isPreTaxFeeCollectionEnabled = useSelector(
    getFeatureToggle(FeatureToggle.PRE_TAX_FEE_COLLECTION),
  )
  const taxes = useSelector(getCurrentBusinessTaxes)

  const list = taxes
    .filter((tax) => !tax.preTaxFee)
    .map((tax) => ({
      ...tax,
      name: `${tax.name} | ${NumberUtils.toPercentFormat(tax.rateValue, 2)}`,
    }))

  const preTaxFeeIdSet = new Set(
    taxes.filter((tax) => tax.preTaxFee === true).map((tax) => tax.id),
  )
  const isPreTaxFeeId = (id: string) => preTaxFeeIdSet.has(id)
  const ids = (value: unknown) =>
    Array.isArray(value) ? (value as string[]) : []

  const onField = ids(field.value)
  const fieldForSelect: Field = {
    ...field,
    value: onField.filter((id) => !isPreTaxFeeId(id)),
    set: ((event: SelectChangeEvent<unknown>) => {
      field.setValue([
        ...ids(field.value).filter(isPreTaxFeeId),
        ...ids(event.target.value),
      ])
    }) as Field['set'],
    setValue: (next: unknown) => {
      const all = ids(next)
      field.setValue([
        ...all.filter(isPreTaxFeeId),
        ...all.filter((id) => !isPreTaxFeeId(id)),
      ])
    },
  }

  return (
    <FormControl fullWidth margin="none">
      <InputLabel htmlFor="tax-rate-select">{field.label}</InputLabel>
      <PuiSelect
        multiple
        field={isPreTaxFeeCollectionEnabled ? fieldForSelect : field}
        input={<Input id="tax-rate-select" />}
        items={list}
        {...rest}
      />
    </FormControl>
  )
}
export default TaxRateSelect
